import { memo, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { Dropdown } from "rsuite";

import CreateQuickCreateDrawer from "../Drawers/CreateQuickCreateDrawer/CreateQuickCreateDrawer";
import useCloudPermissions from "../../hooks/useCloudPermissions";
import QuickNoteModal from "../Sidebar/QuickNote/QuickNoteModal";
import { callFunctionIfExists } from "../../utils/GeneralUtils";
import useGlobals from "../../api/api-hooks/globals/useGlobal";
import DropdownOptionWithIcon from "./DropdownOptionWithIcon";
import ManageItemsDrawer from "../Drawers/ManageItemsDrawer";
import DropdownMenuDivider from "./DropdownMenuDivider";
import { Module } from "../../types/TenantInterfaces";
import CircleIcon from "../../elements/CircleIcon";
import NewButton from "../../elements/NewButton";
import useToggle from "../../hooks/useToggle";
import { IsMobileAtom } from "../../App";

const useQuickCreateMap = ({ quickCreates }: any) => {
  return useMemo(() => {
    if (!Array.isArray(quickCreates) || !quickCreates.length) return {};

    return quickCreates.reduce((obj, qc) => {
      obj[qc.id] = qc;
      return obj;
    }, {});
  }, [quickCreates]);
};

interface Props {
  moduleAPIName: string;
  prefills: any[];
  record: any;
  updating: boolean;
  buttonGroupCSS: string;
  variableIdentifiers?: string[];
  separateMenuTitle?: string;
  onlyOptions?: boolean;
  fieldSchema: any;
  defaultPrefill: any;
  savedPrefillID: string;
  usePageSelection: boolean;
  disableLayouts: boolean;
  brie: boolean;
  setSelectedPrefill: (p: any) => void;
  handleCreate: (data: any) => void;
  handleUpdate: (data: any) => void;
  handleDelete?: () => void;
  handleReorder?: () => void;
  toggleCreateRecords: () => void;
  toggleImportCSV: () => void;
  separateMenuOnClick?: (data: any) => void;
  onSaveAsDefault?: () => void;
  onSaveAsGlobal?: () => void;
  handleAddGlobals: (data: any, onSuccess: () => void) => void;
  publicForm?: boolean;
  currentModule?: Module;
}

const QuickCreateMenu = ({
  moduleAPIName,
  prefills,
  setSelectedPrefill,
  record,
  handleCreate,
  handleUpdate,
  handleDelete,
  handleReorder,
  updating,
  toggleCreateRecords,
  toggleImportCSV,
  buttonGroupCSS,
  variableIdentifiers,
  separateMenuTitle,
  separateMenuOnClick,
  onlyOptions = false,
  fieldSchema,
  defaultPrefill,
  savedPrefillID,
  onSaveAsDefault,
  onSaveAsGlobal,
  handleAddGlobals,
  usePageSelection,
  disableLayouts,
  publicForm = false,
  currentModule,
}: Props) => {
  const isMobile = useAtomValue(IsMobileAtom);
  const whisperRef: any = useRef();
  const [editQuickCreate, setEditQuickCreate] = useState(null);
  const [quickNote, setQuickNote] = useState(false);
  const [newQuickCreate, toggleNewQuickCreate] = useToggle();
  const [manageQuickCreate, toggleManageQuickCreate] = useToggle();
  const { cPermissions } = useCloudPermissions();
  const { globals: quickCreates, globalsLoading: quickCreatesLoading } =
    useGlobals("QuickCreates");

  const qcMap = useQuickCreateMap({ quickCreates });

  const prefillsWithGlobal = useMemo(() => {
    if (
      !onSaveAsGlobal ||
      !Array.isArray(prefills) ||
      !prefills?.length ||
      quickCreatesLoading ||
      !quickCreates
    ) {
      return [];
    }

    return prefills.flatMap((p) => {
      const globalID = p?.GlobalID;

      if (globalID) {
        const globalPrefill = qcMap[globalID];

        if (!globalPrefill) return [];

        return { ...globalPrefill, ...p };
      }

      return p;
    });
  }, [prefills, quickCreates, qcMap, quickCreatesLoading]);

  const Options = () => (
    <Dropdown.Menu style={{ maxWidth: 250 }}>
      <h5 className="px-2 pt-2 text-dark">
        <i className="mdi mdi-lightning-bolt pr-2"></i>Quick Create
      </h5>
      <DropdownMenuDivider />
      {prefills?.map((p) => {
        const globalID = p?.GlobalID;

        if (globalID && quickCreatesLoading) {
          return (
            <DropdownOptionWithIcon
              key={globalID}
              name={`Loading Global QC...`}
              icon={"mdi mdi-earth"}
              onClick={undefined}
            />
          );
        }

        if (globalID && !qcMap[globalID]) return null;

        p = globalID ? { ...(qcMap[globalID] || {}), ...p } : p;

        if (!p) return null;

        return (
          <DropdownOptionWithIcon
            key={p.id}
            name={`New ${p.Name}`}
            onClick={() => {
              setSelectedPrefill(p);
              toggleCreateRecords();
              whisperRef.current.close();
            }}
            icon={p?.Icon ?? "mdi mdi-plus-box"}
            isDefault={p?.id === defaultPrefill?.id}
          />
        );
      })}
      {!prefills?.length && (
        <>
          <Dropdown.Item disabled>
            <div
              className="py-2"
              style={{ whiteSpace: "pre-wrap", color: "var(--medium)" }}
            >
              Add your first Quick Create option in the settings below to get
              started.
            </div>
          </Dropdown.Item>
        </>
      )}
      {moduleAPIName === "Activities" && (
        <>
          <DropdownMenuDivider />
          {/* @ts-ignore */}
          <DropdownOptionWithIcon
            name="Quick Note"
            onClick={() => {
              whisperRef.current.close();
              setQuickNote(true);
            }}
            customIcon={
              <div className="mr-2">
                <CircleIcon icon="mdi mdi-plus" />
              </div>
            }
          />
        </>
      )}
      {toggleImportCSV && !isMobile && (
        <>
          <DropdownMenuDivider />
          <DropdownOptionWithIcon
            name="Bulk Import"
            onClick={() => {
              toggleImportCSV();
              whisperRef.current.close();
            }}
            disabled={!!currentModule?.GlobalModule}
            icon="mdi mdi-upload"
          />
        </>
      )}
      <DropdownMenuDivider />
      {separateMenuTitle ? (
        <DropdownOptionWithIcon
          name={separateMenuTitle}
          onClick={() => {
            callFunctionIfExists(separateMenuOnClick);
            whisperRef.current.close();
          }}
          icon="mdi mdi-cog"
        />
      ) : (
        <DropdownOptionWithIcon
          icon="mdi mdi-cog"
          disabled={!cPermissions.cloud_modify_template}
          name="Quick Create Settings"
          onClick={() => {
            toggleManageQuickCreate();
            whisperRef.current.close();
          }}
        />
      )}
    </Dropdown.Menu>
  );

  return (
    <>
      <NewButton
        onClick={() => {
          if (defaultPrefill) {
            setSelectedPrefill(defaultPrefill);
          }
          toggleCreateRecords();
        }}
        newButtonRef={whisperRef}
        buttonGroupCSS={buttonGroupCSS}
        options={<Options />}
        onlyOptions={onlyOptions}
        disabled={
          (!cPermissions.cloud_create_record && !publicForm) ||
          currentModule?.Dynamic
        }
        onlyNew={publicForm}
      />
      {!separateMenuTitle && (
        <CreateQuickCreateDrawer
          open={newQuickCreate}
          onClose={() => {
            toggleNewQuickCreate();
            setEditQuickCreate(null);
          }}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          afterCreate={toggleNewQuickCreate}
          afterUpdate={() => {
            setEditQuickCreate(null);
            toggleNewQuickCreate();
          }}
          // @ts-ignore
          variableIdentifiers={variableIdentifiers ?? ["user"]}
          updating={updating}
          moduleAPIName={moduleAPIName}
          existingQuickCreate={editQuickCreate}
          recordModuleAPIName={record?._ModuleAPIName}
          fieldSchema={fieldSchema}
          // @ts-ignore
          existingQuickCreates={prefills}
          handleAddGlobals={handleAddGlobals}
          usePageSelection={usePageSelection}
          disableLayouts={disableLayouts}
        />
      )}
      {!separateMenuTitle && (
        <ManageItemsDrawer
          title="Quick Create"
          open={manageQuickCreate}
          items={onSaveAsGlobal ? prefillsWithGlobal : prefills}
          onClose={() => {
            toggleManageQuickCreate();
            whisperRef.current.open();
          }}
          onDelete={handleDelete}
          onReorder={handleReorder}
          onEdit={(val) => {
            setEditQuickCreate(val);
            toggleNewQuickCreate();
          }}
          itemKey="Prefills"
          savedItemID={savedPrefillID}
          onSaveAsDefault={onSaveAsDefault}
          onSaveAsGlobal={onSaveAsGlobal}
          errorMessage={null}
        />
      )}
      {moduleAPIName === "Activities" && (
        // @ts-ignore
        <QuickNoteModal
          open={quickNote}
          setOpen={setQuickNote}
          record={record}
        />
      )}
    </>
  );
};

export default memo(QuickCreateMenu);
